import type { DecodedToken, GameData } from "@/interfaces";
import { getAppId, getReturnDeeplink } from "./token-utils";
import { getGameDataById } from "./game-config-utils";

/**
 * Validates if a URL is valid
 */
function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Options for deeplink resolution
 */
export interface DeeplinkOptions {
  gameId?: string;
  tokenData?: DecodedToken;
  gameData?: GameData;
  fallbackTimeoutMs?: number;
  source?: 'url' | 'storage' | 'any';
}

/**
 * Result of deeplink resolution
 */
export interface DeeplinkResult {
  primaryUrl: string | null;
  fallbackUrl: string | null;
  shouldUseFallback: boolean;
  redirectStrategy: 'deeplink' | 'fallback' | 'none';
}

/**
 * Resolves the best deeplink and fallback URLs based on available data
 */
export function resolveDeeplink(options: DeeplinkOptions = {}): DeeplinkResult {
  const {
    gameId,
    tokenData,
    gameData,
    source = 'any'
  } = options;

  // Get return deeplink from token (highest priority)
  const returnDeeplink = tokenData?.returnDeeplink || getReturnDeeplink(source);
  
  // Validate return deeplink
  if (returnDeeplink && isValidUrl(returnDeeplink)) {
    return {
      primaryUrl: returnDeeplink,
      fallbackUrl: null,
      shouldUseFallback: false,
      redirectStrategy: 'deeplink'
    };
  }

  // Fallback to game configuration deeplinks
  const effectiveGameId = gameId || getAppId(source);
  const effectiveGameData = gameData || (effectiveGameId ? getGameDataById(effectiveGameId) : null);
  
  if (effectiveGameData) {
    const deepLink = effectiveGameData.deep_link;
    const fallbackUrl = effectiveGameData.fallback_url;
    
    if (deepLink && fallbackUrl) {
      return {
        primaryUrl: deepLink,
        fallbackUrl,
        shouldUseFallback: true,
        redirectStrategy: 'deeplink'
      };
    } else if (fallbackUrl) {
      return {
        primaryUrl: fallbackUrl,
        fallbackUrl: null,
        shouldUseFallback: false,
        redirectStrategy: 'fallback'
      };
    }
  }

  // No valid redirect found
  return {
    primaryUrl: null,
    fallbackUrl: null,
    shouldUseFallback: false,
    redirectStrategy: 'none'
  };
}

/**
 * Performs a deeplink redirect with fallback logic
 */
export async function performDeeplinkRedirect(options: DeeplinkOptions = {}): Promise<void> {
  const { fallbackTimeoutMs = 2000 } = options;
  const result = resolveDeeplink(options);

  if (result.redirectStrategy === 'none') {
    console.warn('No valid redirect URL found, staying on current page');
    return;
  }

  if (result.redirectStrategy === 'fallback' || !result.shouldUseFallback) {
    // Direct redirect without fallback logic
    if (result.primaryUrl) {
      window.location.href = result.primaryUrl;
    }
    return;
  }

  // Perform deeplink with fallback
  if (result.primaryUrl && result.fallbackUrl) {
    const timeoutId = setTimeout(() => {
      window.location.href = result.fallbackUrl!;
    }, fallbackTimeoutMs);

    const handleBlur = () => {
      clearTimeout(timeoutId);
      window.removeEventListener("blur", handleBlur);
    };

    window.addEventListener("blur", handleBlur);
    window.location.href = result.primaryUrl;
  }
}

/**
 * Gets the redirect URL for success scenarios
 * Combines token analysis with game configuration fallbacks
 */
export function getSuccessRedirectUrl(options: DeeplinkOptions = {}): string | null {
  const result = resolveDeeplink(options);
  return result.primaryUrl;
}

/**
 * Gets the redirect URL for error/failure scenarios
 * Typically uses fallback URLs rather than deeplinks
 */
export function getErrorRedirectUrl(options: DeeplinkOptions = {}): string | null {
  const result = resolveDeeplink(options);
  
  // For errors, prefer fallback URLs over deeplinks
  if (result.fallbackUrl) {
    return result.fallbackUrl;
  }
  
  // If no fallback, use primary URL as last resort
  return result.primaryUrl;
}

/**
 * Validates if a deeplink redirect is possible with current data
 */
export function canPerformDeeplinkRedirect(options: DeeplinkOptions = {}): boolean {
  const result = resolveDeeplink(options);
  return result.redirectStrategy !== 'none';
}

/**
 * Gets comprehensive redirect information for debugging
 */
export function getRedirectDebugInfo(options: DeeplinkOptions = {}): {
  tokenData: DecodedToken | null;
  gameData: GameData | null;
  resolvedUrls: DeeplinkResult;
  recommendations: string[];
} {
  const { source = 'any' } = options;
  
  const tokenData = options.tokenData || null;
  const gameId = options.gameId || getAppId(source);
  const gameData = options.gameData || (gameId ? getGameDataById(gameId) : null);
  const resolvedUrls = resolveDeeplink(options);
  
  const recommendations: string[] = [];
  
  if (!tokenData) {
    recommendations.push('No token data available - ensure JWT token is present');
  }
  
  if (!gameData) {
    recommendations.push('No game configuration found - check game_config.json');
  }
  
  if (resolvedUrls.redirectStrategy === 'none') {
    recommendations.push('No valid redirect URLs found - check token and configuration');
  }
  
  return {
    tokenData,
    gameData,
    resolvedUrls,
    recommendations
  };
}
